import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Package, Clock, CheckCircle, XCircle, CreditCard, ChevronDown, ShoppingCart } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { API } from "../config";

const STATUS_MAP = {
  pending_payment: { label: "Pendiente de pago", color: "#F57C00", icon: Clock },
  paid: { label: "Pagado", color: "#2E7D32", icon: CheckCircle },
  processing: { label: "En proceso", color: "#1976D2", icon: Package },
  shipped: { label: "Enviado", color: "#7B1FA2", icon: Package },
  delivered: { label: "Entregado", color: "#2E7D32", icon: CheckCircle },
  cancelled: { label: "Cancelado", color: "#D32F2F", icon: XCircle },
};

const OrderHistoryPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);
  const { user, token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) { navigate("/login"); return; }
    const fetchOrders = async () => {
      try {
        const res = await axios.get(`${API}/ecommerce/orders`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setOrders(res.data);
      } catch { /* silent */ }
      finally { setLoading(false); }
    };
    fetchOrders();
  }, [user, token, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#F57C00] border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 bg-[#FAF9F6]" data-testid="order-history-page">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <div className="mb-8">
          <span className="font-mono text-xs font-bold text-[#F57C00] tracking-[0.2em] uppercase">MI CUENTA</span>
          <h1 className="text-3xl font-black text-[#5D4037] mt-2">Mis Pedidos</h1>
        </div>

        {orders.length === 0 ? (
          <div className="bg-white rounded-2xl border border-stone-200 p-12 text-center">
            <ShoppingCart size={48} className="text-stone-300 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-[#5D4037]">No tienes pedidos aún</h3>
            <p className="text-stone-500 mt-2">Visita nuestra tienda para hacer tu primera compra</p>
            <Link to="/tienda" className="btn-primary inline-flex items-center gap-2 mt-6" data-testid="go-shopping-btn">
              <Package size={18} /> Ir a la Tienda
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => {
              const status = STATUS_MAP[order.status] || STATUS_MAP.pending_payment;
              const StatusIcon = status.icon;
              const isExpanded = expandedOrder === order.id;

              return (
                <motion.div
                  key={order.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-2xl border border-stone-200 overflow-hidden"
                  data-testid={`order-${order.order_number}`}
                >
                  <button
                    onClick={() => setExpandedOrder(isExpanded ? null : order.id)}
                    className="w-full p-5 flex items-center justify-between text-left hover:bg-stone-50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${status.color}15` }}>
                        <StatusIcon size={20} style={{ color: status.color }} />
                      </div>
                      <div>
                        <p className="font-bold text-[#5D4037] text-sm">{order.order_number}</p>
                        <p className="text-xs text-stone-400">{new Date(order.created_at).toLocaleDateString("es-MX", { year: "numeric", month: "long", day: "numeric" })}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <span className="text-xs font-semibold px-2 py-1 rounded-full" style={{ backgroundColor: `${status.color}15`, color: status.color }}>
                          {status.label}
                        </span>
                        <p className="text-sm font-bold text-[#5D4037] mt-1">${order.total?.toLocaleString("es-MX", { minimumFractionDigits: 2 })}</p>
                      </div>
                      <ChevronDown size={18} className={`text-stone-400 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                    </div>
                  </button>

                  {isExpanded && (
                    <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} className="border-t border-stone-100">
                      <div className="p-5 space-y-3">
                        {order.items?.map((item, idx) => (
                          <div key={idx} className="flex items-center gap-3 p-3 bg-stone-50 rounded-xl">
                            {item.image && (
                              <img src={item.image} alt={item.name} className="w-12 h-12 rounded-lg object-cover" />
                            )}
                            <div className="flex-1">
                              <p className="font-semibold text-[#5D4037] text-sm">{item.name}</p>
                              <p className="text-xs text-stone-400">
                                {item.pack_type === "pack" ? "Econo-Pak" : "Individual"} × {item.quantity}
                              </p>
                            </div>
                            <span className="font-bold text-sm text-[#5D4037]">
                              ${item.line_total?.toLocaleString("es-MX", { minimumFractionDigits: 2 })}
                            </span>
                          </div>
                        ))}
                        <div className="pt-3 border-t border-stone-200 space-y-1 text-sm">
                          <div className="flex justify-between text-stone-500"><span>Subtotal</span><span>${order.subtotal?.toLocaleString("es-MX", { minimumFractionDigits: 2 })}</span></div>
                          <div className="flex justify-between text-stone-500"><span>IVA</span><span>${order.iva?.toLocaleString("es-MX", { minimumFractionDigits: 2 })}</span></div>
                          <div className="flex justify-between font-bold text-[#5D4037]"><span>Total</span><span>${order.total?.toLocaleString("es-MX", { minimumFractionDigits: 2 })}</span></div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderHistoryPage;
